import { autobind } from 'core-decorators'

class GoogleMapsGeocoderProxyError extends Error {
  constructor(name, message, opts) {
    super(message)
    this.name = `${this.constructor.name}${name}`
    Object.assign(this, opts)
  }
}

const LONGNAME_KEY = 'long_name'

const geocodingAddressComponentMap = {
  postal_code: 'zip',
  street_number: 'number',
  route: 'street',
  administrative_area_level_2: 'region',
  administrative_area_level_1: 'state',
  locality: 'location',
  country: 'country',
}

function validate(googleMapsApi) {
  if (!googleMapsApi) {
    throw new GoogleMapsGeocoderProxyError(
      'ValidationError',
      'GoogleMaps API missing!'
    )
  }

  if (!googleMapsApi.GeocoderStatus) {
    throw new GoogleMapsGeocoderProxyError(
      'ValidationError',
      'GoogleMaps API misses GeocoderStatus property'
    )
  }

  if (!googleMapsApi.Geocoder) {
    throw new GoogleMapsGeocoderProxyError(
      'ValidationError',
      'GoogleMaps API misses Geocoder property'
    )
  }
}

export default
class GoogleMapsGeocoderProxy {

  constructor(googleMapsApi: Object) {
    validate(googleMapsApi)
    this.googleMapsApi = googleMapsApi
    this.geocoder = new googleMapsApi.Geocoder()
  }

  get status() {
    return this.googleMapsApi.GeocoderStatus
  }

  geocode(address: Object) {
    if (!address) {
      throw new GoogleMapsGeocoderProxyError('InputError', 'Address missing!')
    }

    return new Promise((resolve, reject) => {
      this.geocoder.geocode(
        address,
        (results, status) => {
          try {
            const result = this.handleResult({ results, status })
            resolve(result)
            return
          }
          catch (ex) {
            reject(ex)
          }
        }
      )
    })
  }

  @autobind
  handleResult({ results, status }) {
    if (status !== this.googleMapsApi.GeocoderStatus.OK) {
      throw new GoogleMapsGeocoderProxyError(
        'GeocodingError',
        'There was an error geocoding the input',
        {
          status
        }
      )
    }

    const addressComponentsKey = 'address_components'
    const formattedAddressKey = 'formatted_address'

    const mostLikelyMatch = results[0]
    const address = this.decodeAddressComponents(mostLikelyMatch[addressComponentsKey])

    return {
      ...address,
      formatted: mostLikelyMatch[formattedAddressKey],
      latitude: mostLikelyMatch.geometry.location.lat(),
      longitude: mostLikelyMatch.geometry.location.lng()
    }
  }

  /**
   * [decodeAddressComponents description]
   * @method decodeAddressComponents
   * @param  {[type]}                addressComponents [description]
   * @return {[type]}                                  [description]
   * @example
   * {
   *   'address_components': [{
   *     'long_name': '1',
   *     'short_name': '1',
   *     'types': [
   *       'street_number'
   *      ]
   *    },
   *    {
   *      'long_name': 'Burghalde',
   *      'short_name': 'Burghalde',
   *      'types': [
   *        'route'
   *      ]
   *    },
   *    ...
   *    ]
   *  }
   */
  decodeAddressComponents(addressComponents) {
    return addressComponents
      .reduce((memo, component) => {
        component.types.forEach((gmapsType) => {
          const type = this.getValueKey(gmapsType)
          const value = this.getValue(component)
          if (value && type && !memo[type]) {
            memo[type] = value
          }
        })
        return memo
      }, {})
  }

  getValueKey(type) {
    return geocodingAddressComponentMap[type]
  }

  getValue(component) {
    return component[LONGNAME_KEY]
  }
}



// WEBPACK FOOTER //
// ./src/shared/services/GoogleMapsGeocoderProxy.js